import { getNexusClient } from './client'

// Types for mail operations
export interface MailAttachment {
  id?: string
  filename: string
  size: number
  contentType: string
  downloadId?: string // ID for downloading attachment content
}

export interface MailMessage {
  id: string
  uid: string
  folder: string
  from: {
    address: string
    name?: string
  }
  to: { address: string; name?: string }[]
  cc?: { address: string; name?: string }[]
  subject: string
  snippet: string
  bodyText?: string
  bodyHtml?: string
  date: string
  isRead: boolean
  isStarred: boolean
  hasAttachments: boolean
  attachments?: MailAttachment[]
  labels?: string[]
}

export interface MailFolder {
  id: string
  name: string
  path: string
  type: 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive' | 'custom'
  unreadCount: number
  totalCount: number
}

export interface FetchEmailsParams {
  accountId: string
  folder?: string
  limit?: number
  offset?: number
}

export interface FetchEmailsResult {
  messages: MailMessage[]
  totalCount: number
  hasMore: boolean
}

export interface ReadEmailParams {
  accountId: string
  messageUid: string
  folder: string
}

export interface SendEmailParams {
  accountId: string
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  body: string
  isHtml?: boolean
  replyToMessageId?: string
}

export interface EmailActionParams {
  accountId: string
  messageUids: string[]
  action: 'markRead' | 'markUnread' | 'star' | 'unstar' | 'delete' | 'move' | 'archive'
  targetFolder?: string
}

// Edge function invocation helper
async function invokeMailFunction<T>(
  functionName: string,
  payload: Record<string, unknown>
): Promise<{ data: T | null; error: string | null }> {
  try {
    const client = getNexusClient()

    const { data, error } = await client.functions.invoke(functionName, {
      body: payload,
    })

    if (error) {
      console.error(`Mail function ${functionName} error:`, error)
      return { data: null, error: error.message }
    }

    return { data: data as T, error: null }
  } catch (err: any) {
    console.error(`Mail function ${functionName} exception:`, err)
    return { data: null, error: err.message || 'Unknown error' }
  }
}

// Mail API functions
export const mailApi = {
  /**
   * Fetch emails from a folder
   */
  async fetchEmails(params: FetchEmailsParams): Promise<FetchEmailsResult> {
    const { data, error } = await invokeMailFunction<FetchEmailsResult>('mail-fetch', {
      accountId: params.accountId,
      folder: params.folder || 'INBOX',
      limit: params.limit || 50,
      offset: params.offset || 0,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to fetch emails')
    }

    return data
  },

  /**
   * Read a single email with full content
   */
  async readEmail(params: ReadEmailParams): Promise<MailMessage> {
    const { data, error } = await invokeMailFunction<{ message: MailMessage }>('mail-read', {
      accountId: params.accountId,
      messageUid: params.messageUid,
      folder: params.folder,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to read email')
    }

    return data.message
  },

  /**
   * Send an email
   */
  async sendEmail(params: SendEmailParams): Promise<{ success: boolean; messageId?: string }> {
    const { data, error } = await invokeMailFunction<{ success: boolean; messageId?: string }>('mail-send', {
      accountId: params.accountId,
      to: params.to,
      cc: params.cc,
      bcc: params.bcc,
      subject: params.subject,
      body: params.body,
      isHtml: params.isHtml ?? false,
      replyToMessageId: params.replyToMessageId,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to send email')
    }

    return data
  },

  /**
   * Perform action on emails (mark read, star, delete, move)
   */
  async emailAction(params: EmailActionParams): Promise<{ success: boolean }> {
    const { data, error } = await invokeMailFunction<{ success: boolean }>('mail-action', {
      accountId: params.accountId,
      messageUids: params.messageUids,
      action: params.action,
      targetFolder: params.targetFolder,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to perform action')
    }

    return data
  },

  /**
   * Get folder list for an account
   */
  async getFolders(accountId: string): Promise<MailFolder[]> {
    const { data, error } = await invokeMailFunction<{ folders: MailFolder[] }>('mail-folders', {
      accountId,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to fetch folders')
    }

    return data.folders
  },

  /**
   * Sync emails from server (refresh)
   */
  async syncEmails(accountId: string, folder?: string): Promise<{ synced: number }> {
    const { data, error } = await invokeMailFunction<{ synced: number }>('mail-sync', {
      accountId,
      folder: folder || 'INBOX',
    })

    if (error || !data) {
      throw new Error(error || 'Failed to sync emails')
    }

    return data
  },

  /**
   * Download an email attachment
   */
  async downloadAttachment(attachmentId: string): Promise<{ filename: string; contentType: string; content: string }> {
    const { data, error } = await invokeMailFunction<{
      filename: string
      contentType: string
      size: number
      content: string
    }>('mail-attachment', {
      attachmentId,
    })

    if (error || !data) {
      throw new Error(error || 'Failed to download attachment')
    }

    return data
  },
}

// Default folder definitions
export const DEFAULT_FOLDERS: Omit<MailFolder, 'id' | 'unreadCount' | 'totalCount'>[] = [
  { name: 'Inbox', path: 'INBOX', type: 'inbox' },
  { name: 'Sent', path: 'Sent', type: 'sent' },
  { name: 'Drafts', path: 'Drafts', type: 'drafts' },
  { name: 'Archive', path: 'Archive', type: 'archive' },
  { name: 'Spam', path: 'Spam', type: 'spam' },
  { name: 'Trash', path: 'Trash', type: 'trash' },
]
